import { useState } from 'react';
import { useApp, Card, Pill, Select, NakedButton, Icon } from '@/components/ds';
import { CodeBlock, CopyChip, LANG_LABELS } from '@/components/code';
import { genAll, type Endpoint } from '@/lib/codegen';
import { API_META, PAYMENT_CREATE_SPEC } from '@/data';
import { ENVIRONMENT_IDS, ENVIRONMENTS, apiBaseUrl } from '@/config/environments';

// reference-hero.tsx — the API reference page's overview hero: version/title, and the
// Server / Authentication / Client Libraries panels. All content is sourced from real data
// (API_META from the regenerated spec, useApp()'s live environment/key/lang state) — nothing here
// is hand-authored placeholder copy; a field simply doesn't render if the source doesn't have it.

const CLIENT_LANGS = ['curl', 'node', 'python', 'go'] as const;

function ServerBox() {
  const app = useApp();
  const options = ENVIRONMENT_IDS.map((id) => ({ value: id, label: apiBaseUrl(id) }));
  return (
    <Card padding={16}>
      <div className="ref-caption" style={{ marginBottom: 8 }}>
        Server
      </div>
      <Select value={app.env} onChange={(v) => app.setEnv(v as typeof app.env)} options={options} />
      <div className="ref-caption" style={{ marginTop: 6, textTransform: 'none' }}>
        {ENVIRONMENTS[app.env].label}
      </div>
    </Card>
  );
}

function EyeIcon({ open }: { open: boolean }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {open ? (
        <>
          <path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5Z" />
          <circle cx="8" cy="8" r="2" />
        </>
      ) : (
        <>
          <path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5Z" />
          <path d="M2 2l12 12" />
        </>
      )}
    </svg>
  );
}

function AuthBox() {
  const app = useApp();
  const [revealed, setRevealed] = useState(false);
  const display = app.hasKey ? app.apiKey : 'melio_secret_staging01_…';
  const masked = '•'.repeat(Math.min(display.length, 28));
  return (
    <Card padding={16}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span className="ref-body-sm-strong">Authentication</span>
        <span className="ref-caption" style={{ textTransform: 'none' }}>
          apiKey Required
        </span>
      </div>
      {API_META.authDescription && (
        <div className="ref-body-sm" style={{ marginTop: 8 }}>
          {API_META.authDescription}
        </div>
      )}
      <div className="mono" style={{ fontSize: 13, marginTop: 10, color: 'var(--text-secondary)' }}>
        Name: <span style={{ color: 'var(--text-primary)' }}>api-key</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
        <span className="mono" style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          Value:
        </span>
        <span className="mono" style={{ fontSize: 13, color: 'var(--text-primary)', wordBreak: 'break-all' }}>
          {revealed ? display : masked}
        </span>
        <button
          onClick={() => setRevealed((r) => !r)}
          aria-label={revealed ? 'Hide value' : 'Reveal value'}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--icon-primary)', display: 'flex', padding: 2 }}
        >
          <EyeIcon open={revealed} />
        </button>
        <CopyChip text={display} label="" dark={false} />
      </div>
    </Card>
  );
}

function ClientLibrariesBox({ ep }: { ep: Endpoint }) {
  const app = useApp();
  const langs = genAll(ep, app.apiKey, app.apiBase);
  const active = (CLIENT_LANGS as readonly string[]).includes(app.lang) ? app.lang : 'curl';
  return (
    <Card padding={0} style={{ overflow: 'hidden' }}>
      <div className="ref-caption" style={{ padding: '12px 16px 0' }}>
        Client Libraries
      </div>
      <div style={{ display: 'flex', gap: 4, padding: '8px 16px 0', borderBottom: '1px solid var(--border-static)' }}>
        {CLIENT_LANGS.map((k) => (
          <button
            key={k}
            onClick={() => app.setLang(k)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 4px',
              marginBottom: -1,
              borderBottom: active === k ? '2px solid var(--accent)' : '2px solid transparent',
              background: 'none',
              border: 'none',
              borderBottomWidth: 2,
              cursor: 'pointer',
              color: active === k ? 'var(--text-primary)' : 'var(--text-secondary)',
            }}
          >
            <span className={active === k ? 'ref-body-sm-strong' : 'ref-body-sm'} style={{ color: 'inherit' }}>
              {LANG_LABELS[k]}
            </span>
          </button>
        ))}
      </div>
      <CodeBlock
        code={langs[active]}
        lang={active}
        title={LANG_LABELS[active]}
        dark={app.codeDark}
        maxHeight={260}
        style={{ border: 'none', borderRadius: 0 }}
      />
    </Card>
  );
}

function ApiOverview() {
  const hasSpecMeta = Boolean(API_META.version || API_META.oasVersion);
  return (
    <div className="doc-cols" style={{ paddingBottom: 40 }}>
      <div style={{ minWidth: 0 }}>
        {hasSpecMeta && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            {API_META.version && <Pill kind="neutral">{`v${API_META.version.replace(/^v/, '')}`}</Pill>}
            {API_META.oasVersion && <Pill kind="neutral">{`OAS ${API_META.oasVersion}`}</Pill>}
          </div>
        )}
        <h1 className="ref-h1" style={{ margin: 0 }}>
          Melio Partnerships API
        </h1>
        <div style={{ marginTop: 12 }} title={hasSpecMeta ? undefined : 'Coming soon — spec not yet published'}>
          <NakedButton
            rightIcon="arrow-right"
            onClick={hasSpecMeta ? () => window.open('/openapi.yaml', '_blank') : undefined}
            style={!hasSpecMeta ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
          >
            Download OpenAPI Document
          </NakedButton>
        </div>
      </div>
      <div className="doc-aside" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <ServerBox />
        <AuthBox />
        <ClientLibrariesBox ep={PAYMENT_CREATE_SPEC as Endpoint} />
      </div>
    </div>
  );
}

export { ApiOverview, ServerBox, AuthBox, ClientLibrariesBox };
